import React from 'react';
import { DownloadOutlined } from '@ant-design/icons';
import moment from 'moment';
import env from 'api/env';
// Helpers
import { parseForInnerHTML } from 'helpers/textEditor';
import { getEnvLink } from 'helpers/getEnvLink';
import { getArticlePageUrl, getArticlePersonName } from 'helpers/articles';
// Types
import { GetArticleDetails_getArticles_entities } from 'api/articles/types/GetArticleDetails';
import { ArticleStatus, ArticleTypes } from 'api/graphql-global-types';
// Constants
import { commonTimeFormat } from 'constants/global';
// Styles
import styles from './ArticleDetails.module.scss';

type ArticleDetailsProps = {
  record: GetArticleDetails_getArticles_entities | null;
};

const ArticleDetails = ({ record }: ArticleDetailsProps): JSX.Element => {
  const store = record?.store?.storeDetails?.storeName;

  const createdAt = moment(record?.createdAt).format(commonTimeFormat);

  const tagsList = record?.hashtags.map((tag) => tag.name).join(', ');

  const imageLinkUrl = record?.articleUrl;

  const storeUrl = `${getEnvLink(env.REACT_APP_ENV)}/${record?.store?.slug}`;

  const isPublished = record?.status === ArticleStatus.Active;
  const articleUrl = record && isPublished ? getArticlePageUrl(record) : null;

  const isGenerated = record?.type === ArticleTypes.Generated;

  return (
    <div className={styles.root}>
      <div className={styles.dataItem}>
        <div className={styles.label}> Article ID:</div>
        <div> {record?.id}</div>
      </div>

      <div className={styles.dataItem}>
        <div className={styles.label}>Profile selected:</div>
        <a href={storeUrl} target="_blank" rel="noopener noreferrer">
          {store}
        </a>
      </div>

      {articleUrl && (
        <div className={styles.dataItem}>
          <div className={styles.label}>Article URL:</div>
          <a href={articleUrl} target="_blank" rel="noopener noreferrer">
            See Article
          </a>
        </div>
      )}

      <div className={styles.dataItem}>
        <div className={styles.label}>Articles type:</div>
        <div>{isGenerated ? 'Auto-generated' : 'Sponsored Article'}</div>
      </div>

      <div className={styles.dataItem}>
        <div className={styles.label}> Date created:</div>
        <div> {createdAt}</div>
      </div>

      <div className={styles.dataItem}>
        <div className={styles.label}> Status:</div>
        <div> {record?.status}</div>
      </div>

      {isGenerated && (
        <div className={styles.segment}>
          <p className={styles.segmentTitle}>Written from</p>
          <div className={styles.dataItem}>
            <div className={styles.label}>Interview:</div>
            <div>{record?.sourceStream?.name}</div>
          </div>
          <div className={styles.dataItem}>
            <div className={styles.label}>Interviewer:</div>
            <div>{getArticlePersonName(record?.sourceInterviewer || null)}</div>
          </div>
          <div className={styles.dataItem}>
            <div className={styles.label}>Interviewee:</div>
            <div>{getArticlePersonName(record?.sourceInterviewee || null)}</div>
          </div>
        </div>
      )}

      <div className={styles.segment}>
        <p className={styles.segmentTitle}>Article Content</p>
        <div className={styles.dataItem}>
          <div className={styles.label}> Title:</div>
          <div> {record?.title}</div>
        </div>
        {record?.metaDescription && (
          <div className={styles.dataItem}>
            <div className={styles.label}>Meta description:</div>
            <div>{record.metaDescription}</div>
          </div>
        )}
        {!!record?.keyTakeaways?.length && (
          <div className={styles.dataItem}>
            <div className={styles.label}>Key takeaways:</div>
            <ul className={styles.list}>
              {record.keyTakeaways.map((takeaway) => (
                <li key={takeaway}>{takeaway}</li>
              ))}
            </ul>
          </div>
        )}
        <div className={styles.dataItem}>
          <div className={styles.label}> Blog text:</div>
          <div
            className={styles.multilineBody}
            dangerouslySetInnerHTML={{
              __html: parseForInnerHTML(record?.content),
            }}
          ></div>
        </div>
        {!!record?.faq?.length && (
          <div className={styles.dataItem}>
            <div className={styles.label}>FAQ:</div>
            <dl className={styles.list}>
              {record.faq.map(({ question, answer }) => (
                <React.Fragment key={question}>
                  <dt className={styles.question}>{question}</dt>
                  <dd>{answer}</dd>
                </React.Fragment>
              ))}
            </dl>
          </div>
        )}
        <div className={styles.dataItem}>
          <div className={styles.label}>Tags:</div>
          <div> {tagsList}</div>
        </div>
        {record?.url && (
          <div className={styles.dataItem}>
            <div className={styles.label}>Cover image:</div>
            <a href={record.url} target="_blank" rel="noopener noreferrer">
              <DownloadOutlined /> Open image
            </a>
          </div>
        )}
        {record?.imageLabel && (
          <div className={styles.dataItem}>
            <div className={styles.label}> Image label:</div>
            <div> {record.imageLabel}</div>
          </div>
        )}
        {imageLinkUrl && (
          <div className={styles.dataItem}>
            <div className={styles.label}>Image url:</div>
            <a href={imageLinkUrl} target="_blank" rel="noopener noreferrer">
              go to image url
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default ArticleDetails;
